import React from 'react';

import { ActionIcon, Avatar, Box, Card, Flex, Text } from '@mantine/core';
import { IconPlayerPause, IconPlayerPlay, IconSettings } from '@tabler/icons-react';
import { VoiceBadge } from 'components/SmartAssistantSections/PhoneAgentBasicsSection/VoiceBadge';
import { useTranslation } from 'react-i18next';
import { VoiceConfiguration } from 'shared/interfaces/Voice';

interface VoiceCardProps {
  voiceConfiguration: VoiceConfiguration;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  setPlayingVoiceId: (voiceId: string | null) => void;
  onSettingsClick?: () => void;
  onSelect?: () => void;
  maxWidth?: number;
  hideSettings?: boolean;
  isPlaying?: boolean;
  disabled?: boolean;
  description?: string;
}

export function VoiceCard(voiceCardProps: VoiceCardProps) {
  const {
    voiceConfiguration,
    audioRef,
    onSettingsClick,
    onSelect,
    setPlayingVoiceId,
    maxWidth,
    description,
    hideSettings = false,
    isPlaying = false,
    disabled = false
  } = voiceCardProps;
  const { t } = useTranslation();
  const { voiceId, name, language, gender, age, previewUrl, avatarUrl } = voiceConfiguration;

  const handlePlayPause = () => {
    if (!audioRef.current) {
      // If audioRef.current is not set (i.e. this is the first time the VoiceCard is being played),
      // we need to create a new audio element and set its source to the previewUrl
      audioRef.current = new Audio(previewUrl);
      audioRef.current.addEventListener('ended', () => setPlayingVoiceId(null));
    } else {
      // If audioRef.current is already set, we need to update the audio element's source to the previewUrl of the current VoiceCard
      audioRef.current.src = previewUrl;
    }

    // If paused, we need to pause the audio element,  reset its current time to 0 and setPlayingVoiceId to null
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlayingVoiceId(null);
    } else {
      // If playing, we need to play the audio element and setPlayingVoiceId to the voiceId of the current VoiceCard
      audioRef.current.play();
      setPlayingVoiceId(voiceId);
    }
  };

  return (
    <Card
      withBorder
      style={{
        maxWidth,
        cursor: onSelect ? 'pointer' : 'default',
        opacity: disabled ? 0.5 : 1,
        height: '100%'
      }}
      sx={(theme) => ({
        transition: 'box-shadow 150ms ease, transform 150ms ease',
        '&:hover': onSelect
          ? {
              boxShadow: theme.shadows.md,
              transform: 'translateY(2px)'
            }
          : undefined
      })}
      onClick={onSelect}>
      <Flex direction="column" h="100%" gap="md">
        <Flex direction="row" align="flex-start" justify="space-between">
          <Flex direction="row" align="center">
            <Avatar size="lg" radius="xl" src={avatarUrl} mr="sm" />
            <Box>
              <Flex direction="column" gap={6}>
                <Text size="sm" weight={500}>
                  {name}
                </Text>
                <Flex gap="xs" wrap="wrap">
                  <VoiceBadge value={t(`LANGUAGES.${language.toUpperCase()}.READABLE_NAME`)} />
                  <VoiceBadge value={t(`GENDER.${gender.replace('-', '_').toUpperCase()}.READABLE_NAME`)} />
                  <VoiceBadge value={t(`AGE.${age.replace('-', '_').toUpperCase()}.READABLE_NAME`)} />
                </Flex>
              </Flex>
            </Box>
          </Flex>

          <Flex direction="row" gap="xs">
            <ActionIcon
              variant="transparent"
              color="dark.3"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause();
              }}>
              {isPlaying ? <IconPlayerPause size="1rem" /> : <IconPlayerPlay size="1rem" />}
            </ActionIcon>
            {!hideSettings && (
              <ActionIcon variant="transparent" color="dark.3" size="sm" onClick={onSettingsClick}>
                <IconSettings size="1rem" />
              </ActionIcon>
            )}
          </Flex>
        </Flex>

        {description && (
          <Text size="sm" color="gray" opacity={0.8} lineClamp={3}>
            {description}
          </Text>
        )}
      </Flex>
    </Card>
  );
}
